"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-stellar-border rounded-md bg-obsidian-glass/20">
        <p className="font-default text-xl text-lunar-white italic">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-electric-magenta text-electric-magenta hover:bg-electric-magenta hover:text-midnight-void transition-all">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Aria Sterling" className="border-0 border-b border-stellar-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-electric-magenta transition-colors duration-[400ms] font-default text-base text-lunar-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="aria@eclipse-music.com" className="border-0 border-b border-stellar-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-electric-magenta transition-colors duration-[400ms] font-default text-base text-lunar-white" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="border-0 border-b border-stellar-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-electric-magenta transition-colors duration-[400ms] font-default text-base text-lunar-white" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="workshop_date" rules={{
      required: "Date is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
              Preferred Tour Date
            </FormLabel>
            <FormControl>
              <Input type="date" className="border-0 border-b border-stellar-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-electric-magenta transition-colors duration-[400ms] font-default text-base text-lunar-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="participants" rules={{
      required: "Required",
      min: {
        value: 1,
        message: "Min 1"
      },
      max: {
        value: 10,
        message: "Max 10"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
              Guest Count
            </FormLabel>
            <FormControl>
              <Input type="number" min={1} max={10} placeholder="1" className="border-0 border-b border-stellar-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-electric-magenta transition-colors duration-[400ms] font-default text-base text-lunar-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="skill_level" rules={{
      required: "Please select a category"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
              Inquiry Type
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-wrap gap-3 pt-2">
                {["Press", "Booking", "Collaboration"].map((level, i) => <label data-index={i} key={level} className="flex items-center gap-2 px-5 py-2 border border-stellar-border rounded-md cursor-pointer hover:border-electric-magenta transition-colors has-[:checked]:border-electric-magenta has-[:checked]:bg-electric-magenta/10">
                    <RadioGroupItem value={level} className="sr-only" />
                    <span className="font-default text-sm text-lunar-white">{level}</span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="tier_of_interest" rules={{
      required: "Please select a tier"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
              Access Tier
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full border-0 border-b border-stellar-border bg-transparent px-0 h-10 focus-visible:ring-0 focus-visible:border-electric-magenta font-default text-base text-lunar-white">
                  <SelectValue placeholder="Select access level" />
                </SelectTrigger>
                <SelectContent className="bg-obsidian-glass border-stellar-border">
                  <SelectItem value="General">General Admission</SelectItem>
                  <SelectItem value="VIP">VIP Backstage</SelectItem>
                  <SelectItem value="Press">Press Pass</SelectItem>
                  <SelectItem value="Industry">Industry Guest</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta font-medium">
              Project Details or Special Requests
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Tell us about your vision, media outlet, or specific tour requirements." className="border-0 border-b border-stellar-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-electric-magenta transition-colors duration-[400ms] font-default text-base text-lunar-white resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="philosophy_acknowledged" rules={{
      required: "Please confirm you have reviewed the terms"
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-stellar-border data-[state=checked]:bg-electric-magenta data-[state=checked]:border-electric-magenta" />
              </FormControl>
              <FormLabel className="font-default italic text-base text-shadow-blue cursor-pointer leading-relaxed">
                I agree to the terms of the Eclipse digital experience.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-md bg-destructive/5">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-4">
        <Button type="submit" disabled={isSubmitting} className="rounded-md px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90 hover:translate-x-1 transition-all font-default text-base font-bold uppercase tracking-wider">
          {isSubmitting ? "Transmitting..." : "Send Signal"}
          <ArrowRight className="ml-2 size-4" />
        </Button>
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-shadow-blue">
          LAT: 35.6762 / LON: 139.6503 — Signal encrypted.
        </p>
      </div>
    </Form>;
}
export default function JoinTheEclipse() {
  const leftReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const rightReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="5201" id="join-the-eclipse" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftReveal.ref} className={`lg:col-span-5 lg:pt-6 transition-all duration-1000 ease-out ${leftReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-electric-magenta mb-8">
              Direct Transmission
            </p>
            <h2 className="font-default text-4xl md:text-5xl leading-[1.1] text-lunar-white mb-10">
              Step into the
              <br />
              <span className="italic text-electric-magenta">Eclipse</span>.
            </h2>
            <div className="font-default text-base md:text-lg text-shadow-blue leading-relaxed space-y-5 mb-12">
              <p>
                The Eclipse is more than a performance; it is a celestial event. Access to the inner circle and backstage experiences is granted through a curated selection process to maintain the intimacy of the tour.
              </p>
              <p>
                Whether you are looking to book a performance, request a press kit, or inquire about VIP access for upcoming tour dates, please provide your details. Our management team reviews every signal with the precision of a strobe light.
              </p>
              <p>
                The darkness is temporary. The music is eternal. We look forward to seeing you in the void.
              </p>
            </div>

            <div className="border border-stellar-border rounded-md p-6 md:p-7 max-w-md bg-obsidian-glass/40">
              <p className="font-default italic text-base md:text-lg text-lunar-white mb-2">
                Management: contact@the-eclipse.com
              </p>
              <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-shadow-blue">
                Response time: 48-72 hours
              </p>
            </div>
          </div>

          {}
          <div ref={rightReveal.ref} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-150 ${rightReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative border border-stellar-border rounded-md p-8 md:p-14 bg-card backdrop-blur-xl">
              <div className="absolute top-0 left-14 right-14 h-px bg-electric-magenta/60" />
              <FormProvider formKey="join_form_eclipse" formId="join_form_eclipse" sectionName="join-the-eclipse" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              workshop_date: "",
              participants: "",
              skill_level: "",
              tier_of_interest: "",
              special_requests: "",
              philosophy_acknowledged: false
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              workshop_date: {
                required: true
              },
              participants: {
                required: true,
                min: 1,
                max: 10
              },
              skill_level: {
                required: true
              },
              tier_of_interest: {
                required: true
              },
              philosophy_acknowledged: {
                required: true
              }
            }} submitText="Send Signal" successMessage="Signal received. The Eclipse management will respond within 48-72 hours." errorMessage="Transmission failed. Please check your connection or contact us directly.">
                <ReservationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}